import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { UserAvatarMenu } from "../auth/UserAvatarMenu";
import { useAuth } from "../../auth/AuthContext";
import { Logo } from "../brand/Logo";
import { openCalendly } from "../../utils/calendly";
import { logEvent } from "../../utils/analytics";

const navLinks = [
  { label: "How It Works", scrollId: "how-it-works" },
  { label: "Pricing",      to: "/pricing"            },
  { label: "Case Studies", to: "/case-studies"       },
  { label: "Developers",   to: "/developers"         },
];

export function SiteHeader() {
  const { isAuthenticated } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const visibleLinks = navLinks.filter((l) => !l.authOnly || isAuthenticated);

  const handleNavClick = (link) => {
    if (!link.scrollId) return; // regular <Link> handles non-scroll items
    if (location.pathname === '/') {
      document.getElementById(link.scrollId)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      // small delay to let the home page mount before scrolling
      setTimeout(() => document.getElementById(link.scrollId)?.scrollIntoView({ behavior: 'smooth' }), 150);
    }
  };

  return (
    // Sticky header with blurred "glass" background (via opacity + backdrop-blur).
    <header className="fixed top-0 z-50 w-full border-b border-primary/20 bg-background-dark/90 backdrop-blur-xl px-6 md:px-16 py-4 shadow-lg shadow-primary/5">
      <div className="mx-auto flex max-w-7xl items-center justify-between">
        {/* Brand */}
        <Link to="/" className="flex items-center gap-0.5 group cursor-pointer">
          <Logo className="h-7 w-auto transition-all group-hover:scale-110" />
          <h2 className="translate-y-1 text-xl font-bold tracking-tight text-white group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-primary group-hover:via-blue-400 group-hover:to-cyan-400 group-hover:bg-clip-text transition-all">
            euteuristix
          </h2>
        </Link>

        {/* Desktop nav (hidden on small screens) with enhanced hover effects */}
        <nav className="hidden md:flex items-center gap-10">
          {/* How It Works */}
          {visibleLinks.filter((l) => l.scrollId).map((l) => (
            <button
              key={l.label}
              onClick={() => handleNavClick(l)}
              className="text-sm font-semibold text-slate-300 hover:text-primary transition-all relative group"
            >
              {l.label}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-cyan-400 group-hover:w-full transition-all duration-300" />
            </button>
          ))}

          {/* Tools dropdown */}
          <div className="relative group">
            <button className="flex items-center gap-0.5 text-sm font-semibold text-slate-300 hover:text-primary transition-all relative">
              Tools
              <span className="material-symbols-outlined text-[16px] transition-transform duration-200 group-hover:rotate-180">expand_more</span>
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-cyan-400 group-hover:w-full transition-all duration-300" />
            </button>
            <div className="absolute left-0 top-full z-50 pt-3 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-200">
              <div className="w-72 rounded-xl border border-white/10 bg-slate-900 backdrop-blur-xl p-2 shadow-2xl shadow-black/40">
                <Link
                  to="/cbm-calculator"
                  className="flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 hover:bg-white/5 transition group/item"
                >
                  <div>
                    <p className="text-sm font-semibold text-white">CBM Calculator</p>
                    <p className="text-xs text-slate-400">Calculate cargo volume instantly</p>
                  </div>
                  <span className="shrink-0 rounded-full bg-primary/20 px-2 py-0.5 text-[10px] font-bold text-primary">Free</span>
                </Link>
                {/* Featured — zero-offset glow so it halos evenly on all 4
                    sides, not Tailwind's default shadow-lg (which is really
                    just a downward drop-shadow, not a symmetric halo) */}
                {/* Always the Beyond CBM page itself, never /console — this
                    link explicitly names the destination, unlike the
                    generic hero/final-cta "try free"/"open console" buttons.
                    Logged-in users get the same input UI with a "Run in
                    console" handoff instead of the capped free run. */}
                <Link
                  to="/3d-cargo-load-simulator"
                  onClick={() => logEvent({ name: 'nav_tools_beyond_cbm', type: 'cta' })}
                  className="mt-1 flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 shadow-[0_0_20px_4px_rgba(13,127,242,0.35)] transition hover:bg-white/5 hover:shadow-[0_0_28px_6px_rgba(13,127,242,0.55)]"
                >
                  <div>
                    <p className="text-sm font-semibold text-white">Beyond CBM</p>
                    <p className="text-xs text-slate-400">3D load simulation — visualise optimal container packing</p>
                  </div>
                  {!isAuthenticated && (
                    <span className="shrink-0 rounded-full bg-primary/20 px-2 py-0.5 text-[10px] font-bold text-primary">Free tier</span>
                  )}
                </Link>
              </div>
            </div>
          </div>

          {/* Remaining links */}
          {visibleLinks.filter((l) => !l.scrollId).map((l) => (
            <Link
              key={l.label}
              className="text-sm font-semibold text-slate-300 hover:text-primary transition-all relative group"
              to={l.to}
            >
              {l.label}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-cyan-400 group-hover:w-full transition-all duration-300" />
            </Link>
          ))}
        </nav>

        {/* Right side actions */}
        <div className="flex items-center gap-3">
          {/* Examples entry — was a Tools-dropdown line item, pulled again
              (second time this session) since it kept reading as competing
              with Beyond CBM inside that menu. This is deliberately outside
              the dropdown entirely: a standalone icon, game-controller glyph
              (matches the Examples page's own "Play arena" framing), with an
              animate-pulse "flash" so it draws the eye without needing any
              label text to explain itself. */}
          <Link
            to="/examples"
            onClick={() => logEvent({ name: 'nav_examples_icon', type: 'cta' })}
            title="Explore examples — 7 real shipments, already packed"
            className="hidden sm:flex items-center justify-center rounded-lg border border-primary/30 bg-primary/10 p-2 text-primary transition hover:bg-primary/20 hover:border-primary/60"
          >
            <span className="material-symbols-outlined animate-pulse text-xl">sports_esports</span>
          </Link>
          {isAuthenticated ? (
            <Link
              to="/console"
              className="hidden sm:flex items-center gap-2 rounded-lg border border-primary/40 px-4 py-2 text-sm font-bold text-primary hover:bg-primary/10 hover:border-primary/70 transition"
            >
              <Logo className="size-4 text-primary" />
              Console
            </Link>
          ) : (
            <button
              onClick={() => openCalendly('https://calendly.com/sajal-agrawal-feuteuristix')}
              className="hidden sm:flex items-center gap-2 rounded-lg border border-primary/40 px-4 py-2 text-sm font-bold text-primary hover:bg-primary/10 hover:border-primary/70 transition"
            >
              Get a demo
            </button>
          )}
          <UserAvatarMenu />
        </div>
      </div>
    </header>
  );
}


